import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { ApiError } from 'types/ApiError';
import {
	Destination,
	DestinationDto,
	DestinationsDeleteResult,
} from 'types/Destination';
import { DestinationsService } from '../services/destinations';

export const useDestinations = (
	accountId: string,
	options?: {
		onError?: (error: ApiError) => void;
		onCreateSuccess?: (destination: Destination) => void;
		onUpdateSuccess?: (destination: Destination) => void;
		onDeleteSuccess?: (destinationId: string) => void;
		onBulkDeleteSuccess?: (result: DestinationsDeleteResult) => void;
	},
) => {
	const { mutate: updateMutate, isPending: isUpdating } = useMutation<
		void,
		ApiError,
		Destination
	>({
		mutationFn: destination => {
			const { id: destinationId, ...destinationDto } = destination;
			return DestinationsService.updateDestination(
				accountId,
				destinationId,
				destinationDto,
			);
		},
		onSuccess: (r, destination) => {
			if (typeof options?.onUpdateSuccess === 'function') {
				options.onUpdateSuccess(destination);
			}
		},
		onError: e => {
			if (typeof options?.onError === 'function') {
				options.onError(e);
			}
		},
	});

	const { mutate: createMutate, isPending: isCreating } = useMutation<
		{ id: string },
		ApiError,
		DestinationDto
	>({
		mutationFn: destinationDto => {
			return DestinationsService.createDestination(
				accountId,
				destinationDto,
			);
		},
		onSuccess: ({ id }, destination) => {
			if (typeof options?.onCreateSuccess === 'function') {
				options.onCreateSuccess({
					...destination,
					id,
				});
			}
		},
		onError: e => {
			if (typeof options?.onError === 'function') {
				options.onError(e);
			}
		},
	});

	const { mutate: deleteMutate, isPending: isDeleting } = useMutation<
		void,
		ApiError,
		string
	>({
		mutationFn: destinationId => {
			return DestinationsService.deleteDestination(
				accountId,
				destinationId,
			);
		},
		onSuccess: (e, destinationId) => {
			if (typeof options?.onDeleteSuccess === 'function') {
				options.onDeleteSuccess(destinationId);
			}
		},
		onError: e => {
			if (typeof options?.onError === 'function') {
				options.onError(e);
			}
		},
	});

	const { mutate: bulkDeleteMutate, isPending: isBulkDeleting } = useMutation<
		DestinationsDeleteResult,
		ApiError,
		{ destinationIds: string[]; mfaToken: string }
	>({
		mutationFn: ({ destinationIds, mfaToken }) => {
			// um id vai pelo endpoint unitario; o lote so a partir de dois
			if (destinationIds.length === 1) {
				return DestinationsService.deleteDestination(
					accountId,
					destinationIds[0],
					mfaToken,
				).then(() => ({ deleted: destinationIds, failed: [] }));
			}

			return DestinationsService.bulkDeleteDestinations(
				accountId,
				destinationIds,
				mfaToken,
			);
		},
		onSuccess: result => {
			if (typeof options?.onBulkDeleteSuccess === 'function') {
				options.onBulkDeleteSuccess(result);
			}
		},
		onError: e => {
			if (typeof options?.onError === 'function') {
				options.onError(e);
			}
		},
	});

	const createOrUpdateDestination = useCallback(
		(destination: Destination | DestinationDto) => {
			if (!destination.id) {
				createMutate(destination);
				return;
			}

			// HK refatorar chamada
			updateMutate({ ...destination, id: destination.id || '' });
		},
		[createMutate, updateMutate],
	);

	const deleteDestination = useCallback(
		(id: string) => {
			deleteMutate(id);
		},
		[deleteMutate],
	);

	const deleteDestinations = useCallback(
		(destinationIds: string[], mfaToken: string) => {
			bulkDeleteMutate({ destinationIds, mfaToken });
		},
		[bulkDeleteMutate],
	);

	return {
		loading: isUpdating || isCreating || isDeleting,
		isBulkDeleting,
		createOrUpdateDestination,
		deleteDestination,
		deleteDestinations,
	};
};
